import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IVirement, defaultValue } from '../../shared/model/virement.model';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IEcole } from 'app/shared/model/ecole.model';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IVirement>,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  soldeTotal: null,
};

const apiUrl = 'api/virement';

const getEcoleVirement = async virement => {
  const ecoleResponse = await axios.get<IEcole>(`/api/ecoles/${virement.ecoleId}`);
  const ecoleData = ecoleResponse.data;
  return {
    ...virement,
    ecole: ecoleData,
  };
};

export const getAllVirements = createAsyncThunk('virement/fetch_virement_list', async (entity: any) => {
  const requestUrl = `${apiUrl}/list?page=${entity.page}&size=${entity.size}`;
  const response = await axios.get<IVirement[]>(requestUrl);
  const virementWithEcole = await Promise.all(response.data.map(getEcoleVirement));
  const totalItems = response.headers['x-total-count'];
  return { virementWithEcole, totalItems };
});

export const getEcoleVirements = createAsyncThunk('virement/fetch_ecole_virement_list', async (entity: IVirement) => {
  let requestUrl = '';
  let statutVirement;
  let searchUrl = '';
  if (entity.statutVirement) {
    statutVirement = '&statutVirement=' + entity.statutVirement;
    searchUrl += statutVirement;
  }
  if (!entity.statutVirement) {
    requestUrl = `${apiUrl}/${entity.ecoleId}/list?anneeScolaireId=${entity.anneeScolaireId}&page=${entity.page}&size=${entity.size}`;
  } else {
    requestUrl = `${apiUrl}/${entity.ecoleId}/list?anneeScolaireId=${entity.anneeScolaireId}&page=${entity.page}&size=${entity.size}${
      searchUrl ? '&' : '?'
    }${searchUrl}`;
  }
  const response = await axios.get<IVirement[]>(requestUrl);
  const totalItems = response.headers['x-total-count'];
  return { result: response.data, totalItems };
});

export const demandeVirement = createAsyncThunk(
  'virement/demande',
  async (entity: IVirement, thunkAPI) => {
    const result = await axios.post<IVirement>(`${apiUrl}/demande`, entity);
    thunkAPI.dispatch(getEcoleVirements(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getVirement = createAsyncThunk(
  'virement/fetch_virement',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IVirement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const valideVirement = createAsyncThunk(
  'virement/update_virement',
  async (entity: IVirement, thunkAPI) => {
    const result = await axios.put<IVirement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getAllVirements({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const soldeTotalByEcole = createAsyncThunk(
  'virement/get_solde_total',
  async (entity: IVirement, thunkAPI) => {
    const result = await axios.get(`${apiUrl}/factures/solde/${entity.ecoleId}`);
    thunkAPI.dispatch(getEcoleVirements(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// Création du slice Redux
export type VirementState = Readonly<typeof initialState>;
const ScolariteSlice = createSlice({
  name: 'virement',
  initialState: initialState as VirementState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getVirement.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEcoleVirements), (state, action) => {
        state.loading = false;
        state.entities = action.payload.result;
        state.totalItems = action.payload.totalItems;
      })
      .addMatcher(isFulfilled(getAllVirements), (state, action) => {
        state.loading = false;
        state.entities = action.payload.virementWithEcole;
        state.totalItems = action.payload.totalItems;
      })
      .addMatcher(isFulfilled(demandeVirement, valideVirement), (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(soldeTotalByEcole), (state, action) => {
        state.loading = false;
        state.soldeTotal = action.payload.data;
      })
      .addMatcher(isRejected(getEcoleVirements, getAllVirements, soldeTotalByEcole), (state, action) => {
        state.loading = false;
      })
      .addMatcher(isPending(getEcoleVirements, valideVirement, getAllVirements, getVirement, soldeTotalByEcole), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(demandeVirement), state => {
        state.errorMessage = null;
        state.updating = true;
      });
  },
});

export const { reset } = ScolariteSlice.actions;

export default ScolariteSlice.reducer;
